import { supabase } from '../supabase/client';
import { SICHERE_ANHANG_MIME } from '../emails/anhangDatei';

export interface BaumKnoten { gdriveId: string; name: string; pfad: string; kinder: BaumKnoten[] }

// Baut den Ordnerbaum aus gdrive_ordner-Zeilen. Datei-Zeilen (per dokumente.gdrive_file_id
// bekannt) und der Objekt-Root (pfad '') werden ausgefiltert; Waisen landen oben.
export function baueOrdnerBaum(
  rows: Array<{ gdrive_id: string; name: string | null; pfad: string | null }>,
  dateiIds: Set<string>,
): BaumKnoten[] {
  const knoten = new Map<string, BaumKnoten>();
  for (const r of rows) {
    if (!r.gdrive_id || !r.name || !r.pfad || dateiIds.has(r.gdrive_id)) continue;
    knoten.set(r.pfad, { gdriveId: r.gdrive_id, name: r.name, pfad: r.pfad, kinder: [] });
  }
  const wurzeln: BaumKnoten[] = [];
  for (const k of knoten.values()) {
    const i = k.pfad.lastIndexOf('/');
    const eltern = i < 0 ? null : knoten.get(k.pfad.slice(0, i));
    if (eltern) eltern.kinder.push(k);
    else wurzeln.push(k);
  }
  const sortiere = (liste: BaumKnoten[]) => {
    liste.sort((a, b) => a.name.localeCompare(b.name, 'de'));
    liste.forEach((k) => sortiere(k.kinder));
  };
  sortiere(wurzeln);
  return wurzeln;
}

export async function ladeOrdnerBaum(objektId: string): Promise<BaumKnoten[]> {
  const [ordnerRes, dokRes] = await Promise.all([
    supabase.from('gdrive_ordner').select('gdrive_id, name, pfad').eq('objekt_id', objektId),
    supabase.from('dokumente').select('gdrive_file_id').eq('objekt_id', objektId).not('gdrive_file_id', 'is', null),
  ]);
  const dateiIds = new Set(((dokRes.data || []) as Array<{ gdrive_file_id: string }>).map((d) => d.gdrive_file_id));
  return baueOrdnerBaum((ordnerRes.data || []) as Array<{ gdrive_id: string; name: string | null; pfad: string | null }>, dateiIds);
}

export const ORDNER_MIME = 'application/vnd.google-apps.folder';
export interface DriveEintrag { id: string; name: string; mimeType: string; size?: string; modifiedTime?: string }

async function invokeFehlerText(error: unknown): Promise<string> {
  let msg = (error as Error)?.message || 'Anfrage fehlgeschlagen';
  const resp = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
  if (resp && typeof resp.json === 'function') {
    try { const b = await resp.json(); if (b?.error) msg = b.error; } catch { /* ignore */ }
  }
  return msg;
}

// Live-Inhalt eines Drive-Ordners (Unterordner zuerst, dann Dateien, je alphabetisch).
export async function ladeOrdnerInhalt(folderId: string): Promise<DriveEintrag[]> {
  const { data, error } = await supabase.functions.invoke('gdrive', { body: { action: 'list-children', parentId: folderId } });
  if (error) throw new Error(await invokeFehlerText(error));
  const files = ((data?.files ?? []) as DriveEintrag[]);
  return files.sort((a, b) => {
    const ao = a.mimeType === ORDNER_MIME ? 0 : 1;
    const bo = b.mimeType === ORDNER_MIME ? 0 : 1;
    return ao - bo || a.name.localeCompare(b.name, 'de');
  });
}

// Datei-Bytes fuer Vorschau/Download — Blob-Typ hart auf die Allowlist beschraenkt (XSS-Schutz).
export async function ladeDateiBlob(fileId: string, mimeType?: string | null): Promise<Blob> {
  const { data, error } = await supabase.functions.invoke('gdrive', { body: { action: 'datei-inhalt', fileId } });
  if (error) throw new Error(await invokeFehlerText(error));
  let roh: Blob;
  if (data instanceof Blob) roh = data;
  else if (data instanceof ArrayBuffer) roh = new Blob([data]);
  else roh = new Blob([data as BlobPart]);
  const typ = (mimeType || '').toLowerCase().split(';')[0].trim();
  const sicher = SICHERE_ANHANG_MIME.has(typ) ? typ : 'application/octet-stream';
  return new Blob([await roh.arrayBuffer()], { type: sicher });
}

export function istGoogleFormat(mimeType: string | null | undefined): boolean {
  return (mimeType || '').startsWith('application/vnd.google-apps');
}

export function formatiereGroesse(size?: string | null): string {
  const n = Number(size);
  if (!size || !Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1).replace('.', ',')} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

// ── D3: Aktionen ────────────────────────────────────────────────────────────

// Datei in einen Drive-Ordner hochladen (multipart; Cap clientseitig wie Backend: 25 MB).
export async function ladeDateiHoch(parentId: string, datei: File, extra?: { titel?: string; mieterId?: string; eigentuemerId?: string }): Promise<{ fileId: string; dokumentId: string | null }> {
  if (datei.size > 25_000_000) throw new Error(`„${datei.name}" ist größer als 25 MB`);
  const form = new FormData();
  form.append('action', 'datei-upload');
  form.append('parentId', parentId);
  for (const [k, v] of Object.entries(extra ?? {})) if (v) form.append(k, v);
  form.append('datei', datei);
  const { data, error } = await supabase.functions.invoke('gdrive', { body: form });
  if (error) throw new Error(await invokeFehlerText(error));
  return data as { fileId: string; dokumentId: string | null };
}

// Datei verschieben. alterParentId IMMER mitgeben — live gelistete Dateien sind
// meist nicht im gdrive_ordner-Cache, ohne Quelle antwortet das Backend mit 400.
export async function verschiebeEintrag(itemId: string, neuerParentId: string, alterParentId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('gdrive', { body: { action: 'move-item', itemId, neuerParentId, alterParentId } });
  if (error) throw new Error(await invokeFehlerText(error));
}

export async function benenneEintragUm(itemId: string, name: string): Promise<{ neuerPfad: string | null }> {
  const { data, error } = await supabase.functions.invoke('gdrive', { body: { action: 'rename-item', itemId, name } });
  if (error) throw new Error(await invokeFehlerText(error));
  return { neuerPfad: (data?.neuerPfad as string | null | undefined) ?? null };
}

// In den Drive-Papierkorb (wiederherstellbar in Drive) — nie hartes Löschen.
export async function verschiebeInPapierkorb(id: string): Promise<void> {
  const { error } = await supabase.functions.invoke('gdrive', { body: { action: 'papierkorb', id } });
  if (error) throw new Error(await invokeFehlerText(error));
}

// ── D4: Globale Suche ───────────────────────────────────────────────────────

export interface SuchTreffer {
  id: string; name: string; mimeType: string;
  size: string | null; modifiedTime: string | null;
  parentId: string | null; pfad: string | null;
  objekt: { id: string; objektnummer: string; bezeichnung: string } | null;
}

// Globale Namens-Suche ueber die gesamte Drive-Ablage (alle Objekte).
export async function sucheDateien(query: string, ordnerIds?: readonly string[]): Promise<SuchTreffer[]> {
  // #558: mit ordnerIds nur in diesen Ordnern (Unterbaum) suchen
  const { data, error } = await supabase.functions.invoke('gdrive', { body: { action: 'drive-suche', query, ...(ordnerIds?.length ? { ordnerIds } : {}) } });
  if (error) throw new Error(await invokeFehlerText(error));
  return (data?.treffer ?? []) as SuchTreffer[];
}

// ── Dokumente E2: Rückgängig, Ordnerstruktur, Textauszug ──────────────────────

// Aus dem Drive-Papierkorb zurückholen (Rückgängig nach „In den Papierkorb“).
export async function stelleWiederHer(id: string): Promise<{ parentId: string | null }> {
  const { data, error } = await supabase.functions.invoke('gdrive', { body: { action: 'wiederherstellen', id } });
  if (error) throw new Error(await invokeFehlerText(error));
  return { parentId: (data?.parentId as string | null | undefined) ?? null };
}

// Standard-Ordnerbaum 00–06/99 für ein Objekt anlegen (setzt objekte.gdrive_folder_id).
export async function legeOrdnerstrukturAn(objektId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('gdrive', { body: { action: 'provision-objekt', objektId } });
  if (error) throw new Error(await invokeFehlerText(error));
}

// Text für Inhaltssuche/KI nach dem Upload ziehen – im Hintergrund, Fehler sind nicht kritisch.
export function starteTextauszug(dokumentId: string): void {
  void supabase.functions.invoke('dokument-text-extract', { body: { dokument_id: dokumentId } })
    .catch((e: unknown) => console.warn('Textauszug nachgelagert:', e));
}
